// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2025 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

/**
 * Shared styled components for modal dialogs in the Offender Assessment section.
 * Used by EmploymentHistoryModal, DrugHistoryModal, and similar add/edit modals.
 *
 * CALENDAR_BACKGROUND uses iconToDataURI to convert an SVG icon into a data URI
 * string that can be used as a CSS background-image for date picker inputs.
 */

import styled from "styled-components";

import { Icon, IconSVG, iconToDataURI, palette } from "~design-system";

import { DatePickerWrapper } from "../shared/SharedDatePicker.styles";

/** Calendar icon as a data URI for use as input background-image */
export const CALENDAR_BACKGROUND = iconToDataURI(
  <Icon kind={IconSVG["CalendarSimple"]} color={palette.pine4} />,
);

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

export const Header = styled.div`
  display: flex;
  flex-direction: column;
  padding: 2.5rem 2.5rem 1rem 2.5rem;
`;

export const Title = styled.h2`
  color: ${palette.pine1};
  font-family: "Public Sans";
  font-size: 1.5rem;
  font-style: normal;
  font-weight: 500;
  line-height: 120%; /* 1.8rem */
  letter-spacing: -0.03rem;
`;

export const Description = styled.p`
  color: ${palette.slate70};
  font-family: "Public Sans";
  font-size: 0.875rem;
  font-style: normal;
  font-weight: 500;
  line-height: 120%; /* 1.05rem */
  letter-spacing: -0.00875rem;
`;

export const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 0rem 2.5rem 2.5rem 2.5rem;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`;

export const Field = styled.div`
  display: flex;
  flex-direction: column;
`;

export const FieldContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const BaseDatePickerWrapper = styled(DatePickerWrapper)`
  .react-datepicker__input-container input {
    background-repeat: no-repeat;
    background-image: ${CALENDAR_BACKGROUND};
    padding-right: 2.5rem;
  }
`;

export const Footer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0.62rem 2.5rem;
  border-top: 1px solid ${palette.slate20};
`;

export const ErrorMessage = styled.div`
  color: ${palette.signal.error};
  font-family: "Public Sans";
  font-size: 0.875rem;
  line-height: 1.5;
`;

export const TextInput = styled.input`
  display: flex;
  min-height: 2.5rem;
  padding: 0.75rem 1rem;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
  border-radius: 0.5rem;
  border: 1px solid ${palette.slate20};
  font-family: "Public Sans";
  font-size: 0.875rem;
  line-height: 1.5;

  &:focus {
    outline: none;
    border-color: ${palette.pine4};
  }

  &::placeholder {
    color: ${palette.slate40};
  }
`;

export const DateRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`;
